import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'
import cx from 'classnames'

import { ActionsDropdown } from 'components/shared/ActionsDropdown/ActionsDropdown'
import { Button } from 'components/shared/Button/Button'
import { IconButton } from 'components/shared/IconButton/IconButton'
import { ActionsCell } from 'components/shared/Table/common/Cells/ActionsCell/ActionsCell'
import { CandidateCell } from 'components/shared/Table/common/Cells/CandidateCell/CandidateCell'
import { DateCell } from 'components/shared/Table/common/Cells/DateCell/DateCell'
import { JobProgressCell } from 'components/shared/Table/common/Cells/JobProgressCell/JobProgressCell'
import { RecommendationCell } from 'components/shared/Table/common/Cells/RecommendationCell/RecommendationCell'
import { ScoreCell } from 'components/shared/Table/common/Cells/ScoreCell/ScoreCell'
import { TextCell } from 'components/shared/Table/common/Cells/TextCell/TextCell'
import { Table } from 'components/shared/Table/Table'
import { useCandidatesTable } from 'components/pages/Jobs/JobDetails/hooks'
import { percentScore } from 'utils/stringFormat'
import { getColumns } from './model'
import { HIRE_STATUSES } from '../../../model'
import { CandidatesTable } from '../../../CandidatesTable/CandidatesTable'
import candidatesTableStyles from '../../../CandidatesTable/CandidatesTable.module.scss'
import { FileTextIcon } from 'components/shared/Icon/model'

import styles from './TestsTable.module.scss'
import jobStyles from '../../../JobDetails.module.scss'

const mapSortingPropToQueryParam = {
  completed: 'completed',
  email: 'email',
  name: 'name',
  status: 'status',
  GMA: 'gma_score',
  Personality: 'personality_score',
  'Ebber Test Score': 'test_score',
}

export const TestsTable = ({
  projectId,
  modules,
  search,
  filter,
  setFilter,
  tab,
  getActions,
  onSelectForInterview,
  onTestsReport,
  handleFavorite,
  hasInterviewModule,
  active,
}) => {
  const columns = getColumns()

  const status = React.useMemo(() => ['completed', 'started'], [])

  const {
    state: {
      table: {
        headers,
        model,
        rowSelection,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
      },
      pagination: { currentPage, pageCount, perPage },
    },
    handlers: {
      table: { toggleAllRowsSelectedHandler },
      pagination: { handlePage, handlePageSize },
    },
  } = useCandidatesTable({
    columns,
    mapSortingPropToQueryParam,
    projectId,
    search,
    filter,
    tab,
    status,
  })

  return (
    <CandidatesTable
      {...{
        active,
        headers,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
        currentPage,
        pageCount,
        perPage,
        toggleAllRowsSelectedHandler,
        handlePage,
        handlePageSize,
        filter,
        setFilter,
      }}
      placeholder={{
        title: t`No candidates in this stage`,
        text: t`Candidates will show up here when they take the tests`,
      }}
    >
      {R.map(
        ({
          id,
          original,
          getIsSelected,
          getIsSomeSelected,
          getToggleSelectedHandler,
        }) => {
          const {
            email,
            name,
            recommendations,
            scores,
            selection_stage,
            status,
            timestamps,
            hire_status,
            is_favourite,
            id: candidateId,
          } = original

          const actions = getActions({ candidate: original })

          return (
            <Table.Row
              key={id}
              type="body"
              onChange={getToggleSelectedHandler()}
              isChecked={getIsSelected()}
              isIndeterminate={getIsSomeSelected()}
              isSelected={rowSelection[id]}
              isHighlighted={hire_status === HIRE_STATUSES.HIRED}
              hasCheckbox
              isDisabled={!active}
              className={jobStyles.row}
            >
              <CandidateCell
                email={email}
                name={name}
                className={active ? jobStyles.favoriteIcon : undefined}
                isFavorite={is_favourite}
                setFavorite={handleFavorite(candidateId)}
                active={active}
              />
              <TextCell text={percentScore(scores['GMA'])} />
              <TextCell text={percentScore(scores['Personality'])} />
              <ScoreCell score={scores['Ebber Test Score']} />
              <RecommendationCell recommendation={recommendations?.test} />
              <DateCell
                date={timestamps?.completed || timestamps?.started}
                hasNowLabel
              />
              <JobProgressCell
                progress={selection_stage - 1}
                status={status}
                type="candidate"
                {...{ hasInterviewModule }}
              />
              <ActionsCell className={styles.actionsCell}>
                {modules.interview && (
                  <Button
                    className={cx(
                      styles.button,
                      styles.selectForInterviewButton,
                    )}
                    color="primary"
                    label={t`Move to interview`}
                    size="small"
                    variant="filled"
                    isDisabled={!active}
                    onClick={() =>
                      onSelectForInterview({ candidate: original })
                    }
                  />
                )}
                <IconButton
                  className={cx(
                    styles.button,
                    candidatesTableStyles.fileTextRowIcon,
                  )}
                  icon={FileTextIcon}
                  size="medium"
                  title={t`View report`}
                  variant="text"
                  onClick={() => onTestsReport({ candidate: original })}
                />
                <ActionsDropdown
                  anchorClassName={styles.button}
                  size="medium"
                  isDisabled={!active}
                  {...{ actions }}
                />
              </ActionsCell>
            </Table.Row>
          )
        },
        model.rows,
      )}
    </CandidatesTable>
  )
}
